import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FormContainer,
  useForm,
  TextFieldElement,
  FieldValues,
} from "react-hook-form-mui";
import AddressInfo from "./AddressInfo";
import useRegisterN01User from "src/welcomeNonGemi/data-access/useRegisterQuery";
import ReCAPTCHA from "react-google-recaptcha";
import pako from "pako";
import { N01RegistrationValidationSchema } from "./validationSchema";
import { LoadingButton } from "@mui/lab";
import { checkAFM } from "src/applications/shared/lib/utils";
import { useEffect, useState } from "react";
import { useGetIdentityTypesForNonGemiRegistration } from "src/welcomeNonGemi/data-access/useGetIdentityTypesForNonGemiRegistration";
import CloseIcon from "@mui/icons-material/Close";
import { useCheckPassport } from "src/shared/data-access/wsConsumers/useCheckPassport";
import { useSearchIdentitiesById } from "src/shared/data-access/wsConsumers/useSearchIdentitiesById";
import { IdentityTypesEnum } from "src/shared/enums/sharedEnums/IdentityTypesEnum";
import SearchIcon from "@mui/icons-material/Search";

const N01RegistrationForm = () => {
  const { t } = useTranslation();
  const formContext = useForm({
    resolver: yupResolver(N01RegistrationValidationSchema()),
  });
  const { mutate, isLoading: isLoadingRegister } = useRegisterN01User();

  const handleRecaptcha = async (value: any) => {
    const compressedString = pako.gzip(value);
    const base64String = btoa(
      String.fromCharCode.apply(null, [...new Uint8Array(compressedString)]),
    );
    formContext.setValue("recaptcha", base64String);
  };
  const [openSelectIdentityTypeModal, setOpenSelectIdentityTypeModal] =
    useState<boolean>(false);
  const { data: identityTypes, isLoading: isLoadingIdentityTypes } =
    useGetIdentityTypesForNonGemiRegistration(openSelectIdentityTypeModal);
  const [identityType, setIdentityType] = useState<number | null>(null);
  const [name, setName] = useState<string>(
    formContext.getValues("n01_firstname") ?? "",
  );
  const [surname, setSurname] = useState<string>(
    formContext.getValues("n01_lastname") ?? "",
  );
  const { mutate: checkPassport, isLoading: isLoadingCheckPassport } =
    useCheckPassport(setOpenSelectIdentityTypeModal);
  const { mutate: checkIdentity, isLoading: isLoadingCheckIdentity } =
    useSearchIdentitiesById();
  const handleSearch = () => {
    const value = formContext.getValues("n01_adtpassport")
      ? formContext.getValues("n01_adtpassport")?.trim()
      : null;
    switch (identityType) {
      case IdentityTypesEnum.POLICE_IDENTITY_CARD:
        checkIdentity({
          adt: value,
        });
        return;
      case IdentityTypesEnum.PASSPORT:
        checkPassport({
          passportNumber: value,
          name: name ? name.trim() : null,
          surname: surname ? surname.trim() : null,
        });
        return;
    }
  };
  useEffect(() => {
    if (
      identityType === IdentityTypesEnum.PASSPORT &&
      openSelectIdentityTypeModal
    ) {
      setName(formContext.getValues("n01_firstname") ?? "");
      setSurname(formContext.getValues("n01_lastname") ?? "");
    } else {
      setName("");
      setSurname("");
    }
  }, [identityType, openSelectIdentityTypeModal]);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
      }}
    >
      <Box>
        <Typography variant="h6" fontWeight="500">
          {t("Στοιχεία Χρήστη (με αστερίσκο τα υποχρεωτικά πεδία)")}
        </Typography>
      </Box>
      <FormContainer
        formContext={formContext}
        onSuccess={async (data: FieldValues) => {
          if (data.n01_afm && !checkAFM(data.n01_afm)) {
            enqueueSnackbar(t("Μη έγκυρο ΑΦΜ"), {
              variant: "error",
            });
            return;
          }
          const formData = new FormData();
          Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append("regType", "individual");
          mutate(formData);
        }}
        onError={(formError) => {
          Object.entries(formError).forEach(([key, value]) => {
            value?.message
              ? enqueueSnackbar(value?.message, {
                  variant: "error",
                })
              : null;
          });
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Grid
            container
            spacing={2}
            lg={9}
            rowSpacing={4}
            sx={{
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Όνομα")}
                name="n01_firstname"
                required
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement label={t("Πατρώνυμο")} name="n01_fathersname" />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Επίθετο")}
                name="n01_lastname"
                required
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement label={t("Email")} name="n01_email" required />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Επιθυμητό Username")}
                name="n01_username"
                required
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Password")}
                name="n01_password1"
                required
                type="password"
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Password Επιβεβαίωση")}
                name="n01_password2"
                required
                type="password"
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("ΑΔΤ/Διαβατήριο")}
                name="n01_adtpassport"
                InputProps={{
                  endAdornment: (
                    <>
                      {import.meta.env
                        .VITE_WSCONSUMERS_IDENTITY_VERIFICATION_ACTIVE ===
                        "true" &&
                        (import.meta.env.VITE_WSCONSUMERS_EMIGRANTS_ACTIVE ===
                          "true" ||
                          import.meta.env
                            .VITE_WSCONSUMERS_POLICE_PASSPORT_ACTIVE ===
                            "true") && (
                          <InputAdornment position="end">
                            <Tooltip title={t("Έλεγχος εγκυρότητας")}>
                              <Button
                                disabled={
                                  !formContext.getValues("n01_adtpassport")
                                }
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() =>
                                  setOpenSelectIdentityTypeModal(true)
                                }
                              >
                                <SearchIcon />
                              </Button>
                            </Tooltip>
                          </InputAdornment>
                        )}
                    </>
                  ),
                }}
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement label={t("ΑΦΜ")} name="n01_afm" />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Τηλέφωνο")}
                name="n01_phone"
                required
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement label={t("Κινητό")} name="n01_mobilephone" />
            </Grid>
          </Grid>
        </Box>
        <AddressInfo />
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            gap: 2,
          }}
        >
          <ReCAPTCHA
            onChange={handleRecaptcha}
            sitekey={import.meta.env.VITE_REACT_APP_SITE_KEY}
          />

          <LoadingButton
            variant="contained"
            type="submit"
            loading={isLoadingRegister}
            sx={{ height: "60%" }}
            disabled={!formContext.watch("recaptcha")}
          >
            {t("Εγγραφή")}
          </LoadingButton>
        </Box>
      </FormContainer>
      <Dialog
        maxWidth="md"
        fullWidth
        open={openSelectIdentityTypeModal}
        onClose={() => {}}
      >
        <DialogTitle>{t("Έλεγχος εγκυρότητας Ταυτότητας")}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpenSelectIdentityTypeModal(false)}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              mx: "auto",
              mb: "2rem",
            }}
          >
            <Typography variant={"body1"}>
              {t(
                "Για να πραγματοποιηθεί έλεγχος εγκυρότητας Διαβατηρίου ή Αριθμού Δελτίου Ταυτότητας πρέπει να εισαχθεί ο τύπος Ταυτότητας",
              )}
            </Typography>

            {!isLoadingIdentityTypes ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "1rem",
                  mx: "auto",
                  mb: "2rem",
                  width: "100%",
                }}
              >
                {identityTypes && (
                  <Autocomplete
                    fullWidth={true}
                    value={identityType}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth={true}
                        label={t("Τύπος Ταυτότητας")}
                      />
                    )}
                    onChange={(_, value) => setIdentityType(value ?? null)}
                    options={identityTypes?.data.map(
                      (identityType) => identityType.id,
                    )}
                    isOptionEqualToValue={(option, value) => option === value}
                    getOptionLabel={(e) =>
                      identityTypes?.data?.find(
                        (identityType) => identityType.id === e,
                      )?.description || ""
                    }
                  />
                )}
                {identityType === IdentityTypesEnum.PASSPORT && (
                  <>
                    <TextField
                      value={name}
                      label={t("Όνομα για διασταύρωση Διαβατηρίου")}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth={true}
                    />
                    <TextField
                      value={surname}
                      label={t("Επώνυμο για διασταύρωση Διαβατηρίου")}
                      onChange={(e) => setSurname(e.target.value)}
                      fullWidth={true}
                    />
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <>
            <Button
              variant={"contained"}
              color={"error"}
              onClick={() => setOpenSelectIdentityTypeModal(false)}
            >
              {t("Ακύρωση")}
            </Button>
            <LoadingButton
              disabled={!formContext.watch("n01_adtpassport")}
              loading={
                isLoadingIdentityTypes ||
                isLoadingCheckIdentity ||
                isLoadingCheckPassport
              }
              variant={"contained"}
              color={"primary"}
              onClick={handleSearch}
            >
              {t("Έλεγχος εγκυρότητας")}
            </LoadingButton>
          </>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default N01RegistrationForm;
